import { useState, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { getPlayerAvatar } from '../../utils/player';
import ImageUpload from '../ImageUpload/ImageUpload';
import ModalCloseButton from './ModalCloseButton';

const EditPlayerModal = ({
  player,
  allPlayers = [],
  clubMembers = [],
  onClose,
  onSave,
  user,
  onDeleteAccount,
  ownedClubs = [],
}) => {
  const [newName, setNewName] = useState(player?.name || '');
  const [newPhotoUrl, setNewPhotoUrl] = useState(player?.photoUrl || '');
  const [error, setError] = useState('');
  
  // Deletion flow step: 0 = edit form, 1 = Transfer ownership, 2 = Are you sure?
  const [deleteStep, setDeleteStep] = useState(0);
  const [selectedNewOwners, setSelectedNewOwners] = useState({});
  const [typedCode, setTypedCode] = useState('');

  // 4-digit security code for account deletion confirmation
  const securityCode = useMemo(() => {
    return Math.floor(1000 + Math.random() * 9000).toString();
  }, []);

  const isSelf = Boolean(
    (user?.uid && player?.uid === user.uid) ||
    (user?.displayName && player?.name && player.name.toLowerCase().trim() === user.displayName.toLowerCase().trim())
  );

  const extractName = (val) => {
    if (typeof val === 'string') return val;
    if (val && typeof val === 'object') {
      if (typeof val.name === 'string') return val.name;
      if (typeof val.displayName === 'string') return val.displayName;
    }
    return '';
  };

  const extractPhoto = (val) => {
    if (val && typeof val === 'object') {
      return val.photoUrl || val.photoURL || null;
    }
    return null;
  };

  const getCandidatesForClub = (club) => {
    const list = [];
    const addedNames = new Set();
    const currentUserDisplayName = (user?.displayName || '').toLowerCase().trim();

    // 1. Add from club.members
    if (club.members && typeof club.members === 'object') {
      Object.entries(club.members).forEach(([key, val]) => {
        const nameStr = extractName(val) || (typeof key === 'string' && !key.startsWith('uid') ? key : '');
        const uidStr = typeof key === 'string' ? key : (val?.uid || nameStr);

        if (nameStr && uidStr && uidStr !== user?.uid && nameStr.toLowerCase().trim() !== currentUserDisplayName) {
          if (!addedNames.has(nameStr.toLowerCase())) {
            list.push({
              uid: String(uidStr),
              name: String(nameStr),
              photoUrl: extractPhoto(val),
            });
            addedNames.add(nameStr.toLowerCase());
          }
        }
      });
    }

    // 2. Add from club.players or allPlayers
    if (Array.isArray(club.players)) {
      club.players.forEach((item) => {
        const nameStr = extractName(item);
        if (nameStr && nameStr.toLowerCase().trim() !== currentUserDisplayName && !addedNames.has(nameStr.toLowerCase())) {
          const match = (allPlayers || []).find((p) => p.name && p.name.toLowerCase().trim() === nameStr.toLowerCase());
          list.push({
            uid: String(match?.uid || nameStr),
            name: String(nameStr),
            photoUrl: match?.photoUrl || null,
          });
          addedNames.add(nameStr.toLowerCase());
        }
      });
    }

    return list;
  };

  // Initialize selected new owners when Step 1 opens
  useEffect(() => {
    if (deleteStep === 1 && ownedClubs && ownedClubs.length > 0) {
      const initialMap = {};
      ownedClubs.forEach((club) => {
        const candidates = getCandidatesForClub(club);
        if (candidates.length > 0) {
          initialMap[club.id] = candidates[0].uid;
        }
      });
      setSelectedNewOwners(initialMap);
    }
  }, [deleteStep, ownedClubs, user]);

  const handleStartDeleteFlow = () => {
    if (ownedClubs && ownedClubs.length > 0) {
      setDeleteStep(1); // Go to Step 1: Transfer ownership
    } else {
      setDeleteStep(2); // Skip directly to Step 2: Are you sure?
    }
  };

  const handleSave = () => {
    const trimmedName = newName.trim();
    if (!trimmedName) {
      setError('Player name cannot be empty.');
      return;
    }

    if (trimmedName.toLowerCase() !== (player?.name || '').toLowerCase()) {
      const existingPlayer = allPlayers.find(
        (item) => item.name.toLowerCase() === trimmedName.toLowerCase()
      );
      if (existingPlayer && existingPlayer.name !== player?.name) {
        setError('This player name already exists in the club.');
        return;
      }
    }

    onSave(player, trimmedName, newPhotoUrl.trim());
    onClose();
  };

  const displayImageUrl = newPhotoUrl || getPlayerAvatar(allPlayers, newName);
  const isCodeCorrect = typedCode.trim() === securityCode;

  return createPortal(
    <div className="modal-overlay">
      <div className="modal">
        {/* Subtle Delete Account icon at top-left in line with close button */}
        {isSelf && deleteStep === 0 && (
          <button
            onClick={handleStartDeleteFlow}
            className="modal-delete"
            aria-label="Delete account"
            title="Delete Account"
          >
            <svg
              xmlns="http://www.w3.org/2000/svg"
              fill="none"
              viewBox="0 0 24 24"
              strokeWidth={1.75}
              stroke="currentColor"
              className="w-6 h-6"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"
              />
            </svg>
          </button>
        )}

        <ModalCloseButton onClick={onClose} />

        {/* STEP 1: TRANSFER OWNERSHIP */}
        {deleteStep === 1 ? (
          <div>
            <h3 className="modal-title">
              Transfer ownership
            </h3>

            <p className="modal-text" style={{ marginBottom: 24 }}>
              Before deleting your account you need transfer ownership of the following clubs to another player.
            </p>

            <div className="space-y-4 mb-6">
              {ownedClubs.map((club) => {
                const candidates = getCandidatesForClub(club);
                const currentSelectedUid = selectedNewOwners[club.id] || (candidates[0] ? candidates[0].uid : '');
                const selectedCandidate = candidates.find((c) => c.uid === currentSelectedUid) || candidates[0];
                const selectedName = selectedCandidate ? String(selectedCandidate.name) : 'Select player';
                const selectedPhoto = selectedCandidate ? selectedCandidate.photoUrl : null;

                return (
                  <div key={club.id} className="text-center">
                    <p className="body-medium font-bold text-white mb-2">{club.name}</p>

                    {candidates.length > 0 ? (
                      <div className="select-field select-field--rich">
                        <div className="flex items-center gap-3">
                          <img
                            src={selectedPhoto || getPlayerAvatar(allPlayers, selectedName)}
                            alt={selectedName}
                            className="w-9 h-9 rounded-full object-cover border border-white/20"
                          />
                          <span className="font-bold text-white">{selectedName}</span>
                        </div>

                        <span className="material-symbols-rounded select-field__icon" aria-hidden="true">
                          keyboard_arrow_down
                        </span>

                        <select
                          value={currentSelectedUid}
                          onChange={(e) =>
                            setSelectedNewOwners((prev) => ({ ...prev, [club.id]: e.target.value }))
                          }
                          className="select-field__overlay"
                          aria-label={`New owner of ${club.name}`}
                        >
                          {candidates.map((c) => (
                            <option key={String(c.uid)} value={String(c.uid)} className="bg-slate-900 text-white">
                              {String(c.name)}
                            </option>
                          ))}
                        </select>
                      </div>
                    ) : (
                      <p className="caption text-amber-400 bg-amber-500/10 p-2.5 rounded-xl border border-amber-500/20">
                        No other members in this club.
                      </p>
                    )}
                  </div>
                );
              })}
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setDeleteStep(0)}
              >
                <span className="btn-text">CANCEL</span>
              </button>
              <button
                type="button"
                className="btn btn-primary"
                onClick={() => setDeleteStep(2)}
              >
                <span className="btn-text">NEXT</span>
              </button>
            </div>
          </div>
        ) : deleteStep === 2 ? (
          /* STEP 2: ARE YOU SURE? (FINAL CONFIRMATION WITH 4-DIGIT CODE) */
          <div>
            <h3 className="modal-title">
              Are you sure?
            </h3>

            <div className="modal-body">
              <p className="modal-text">
                This will delete all player data and you will be removed from all clubs you have joined.
              </p>
              <p className="modal-text modal-text-strong">
                Once confirmed this can't be undone.
              </p>

              <div className="mt-4 pt-2 border-t border-white/10 text-left">
                <p className="caption text-slate-300 mb-2 text-center">
                  Enter security code <strong className="text-white bg-slate-800 px-2 py-0.5 rounded border border-white/20">{securityCode}</strong> to confirm:
                </p>
                <input
                  type="text"
                  maxLength={4}
                  placeholder="Enter 4-digit code"
                  value={typedCode}
                  onChange={(e) => setTypedCode(e.target.value)}
                  className="input text-center text-lg tracking-widest w-full"
                />
              </div>
            </div>

            <div className="modal-footer">
              <button
                type="button"
                className="btn btn-secondary"
                onClick={() => setDeleteStep(0)}
              >
                <span className="btn-text">CANCEL</span>
              </button>
              <button
                type="button"
                disabled={!isCodeCorrect}
                className="btn btn-danger"
                onClick={() => {
                  if (onDeleteAccount) onDeleteAccount(selectedNewOwners);
                  onClose();
                }}
              >
                <span className="btn-text">DELETE</span>
              </button>
            </div>
          </div>
        ) : (
          /* STANDARD EDIT PLAYER / ACCOUNT SETTINGS FORM */
          <>
            <h3 className="modal-title">
              {isSelf ? 'Account Settings' : 'Edit Player Details'}
            </h3>

            {error && <p className="text-red-500 caption mb-md">{error}</p>}

            <div className="modal-content">
              <div className="space-y-4">
                <input
                  type="text"
                  placeholder="Player Name"
                  value={newName}
                  onChange={(e) => {
                    setNewName(e.target.value);
                    setError('');
                  }}
                  className="input"
                  title="Edit player name"
                />
                
                <div className="flex flex-col items-center gap-md">
                  <ImageUpload
                    currentImageUrl={displayImageUrl}
                    onImageChange={setNewPhotoUrl}
                    type="player"
                    id={newName || player?.name}
                    aspect={1}
                    circularCrop={true}
                  />
                </div>
              </div>
            </div>

            <div className="mt-lg flex flex-col gap-md">
              <button
                onClick={handleSave}
                disabled={!newName.trim()}
                className="btn btn-primary btn-full"
              >
                <span className="btn-text">SAVE CHANGES</span>
              </button>
            </div>

            <p className="card-content text-xs mt-md text-center">
              Note: Name changes will update all club and session history data.
            </p>
          </>
        )}
      </div>
    </div>,
    document.body
  );
};

export default EditPlayerModal;
